import * as React from 'react'
import { Loader2, Mail, Shield, Trash2, UserCheck, UserMinus, UserPlus, UserX } from 'lucide-react'
import type { AssignableRolesDto, UserDto } from '@lib/dto/user.dto'
import type { VenueDto } from '@lib/dto/venue.dto'
import { useAuth } from '@/auth/AuthContext'
import { can, reachesCompany, worksAtAssignedVenues } from '@/auth/access'
import { isOrganizationAdmin } from '@/auth/roles'
import { useAssignVenueManager, useUnassignVenueManager, useVenueManagers } from '@/hooks/useVenues'
import { useSwitchableVenues } from '@/hooks/useSwitchableVenues'
import {
  useAssignableRoles,
  useDeactivateUser,
  useDeleteInvitedUser,
  useReactivateUser,
  useResendInvite,
  useUpdateUser,
  useUsers,
} from '@/hooks/useUsers'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { RowActions, type RowAction } from '@/components/ui/row-actions'
import { Select } from '@/components/ui/select'
import { FormError } from '@/components/ui/form-error'
import { UserStatusBadge, inviteActionLabel } from '@/components/UserStatusBadge'
import { PersonInviteForm } from '@/components/people/PersonInviteForm'
import { RolePicker, roleReachHint } from '@/components/people/RolePicker'
import { managesPerson, personRoleKeyOf, personRoleLabel, roleInputOf, type PersonRoleKey } from '@/components/people/personRole'
import { possessive } from '@lib/helpers/format'

/** Change someone's role from the venue they work at. */
function ChangeRoleDialog({
  person,
  roles,
  open,
  onOpenChange,
}: {
  person: UserDto
  roles: AssignableRolesDto
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const saved = personRoleKeyOf(person, roles)
  const [role, setRole] = React.useState<PersonRoleKey | null>(saved)
  const update = useUpdateUser()
  const [error, setError] = React.useState<string | null>(null)

  function save(): void {
    setError(null)
    if (role === null || role === saved) {
      onOpenChange(false)
      return
    }
    update.mutate({ id: person.id, input: roleInputOf(role) }, { onSuccess: () => onOpenChange(false), onError: (e) => setError(requestErrorMessage(e)) })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change {possessive(person.name)} role</DialogTitle>
          <DialogDescription>Their role decides what they can see and do, here and at any other venue they work at.</DialogDescription>
        </DialogHeader>
        <Field label="Role" htmlFor={`role-${person.id}`} hint={roleReachHint(role, roles)}>
          <RolePicker id={`role-${person.id}`} value={role} onChange={setRole} roles={roles} current={saved} />
        </Field>
        {error ? <FormError>{error}</FormError> : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button pending={update.isPending} type="button" onClick={save}>
            Save role
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * One person at the venue: who they are, their role, and what the viewer may do
 * about them. `manageable`: the viewer may change them here (their invitation,
 * this venue). `everywhere`: also wherever else they work, which their role and
 * their account apply to, so only someone who reaches all their venues changes
 * those (api `reachesAllOf`).
 */
function StaffRow({
  venueId,
  person,
  roles,
  manageable,
  everywhere,
}: {
  venueId: string
  person: UserDto
  roles: AssignableRolesDto
  manageable: boolean
  everywhere: boolean
}) {
  const [changingRole, setChangingRole] = React.useState(false)
  const unassign = useUnassignVenueManager()
  const deactivate = useDeactivateUser()
  const reactivate = useReactivateUser()
  const deleteInvited = useDeleteInvitedUser()
  const resend = useResendInvite()
  const [resent, setResent] = React.useState(false)
  // One sentence for the row, cleared as the next action starts: the first of
  // several mutations' errors stayed up after a later action had gone through.
  const [failure, setFailure] = React.useState<string | null>(null)
  const said = { onSuccess: () => setFailure(null), onError: (e: Error) => setFailure(requestErrorMessage(e)) }

  /** Start an action with the row's last error cleared. */
  function act(run: () => void): () => void {
    return () => {
      setFailure(null)
      run()
    }
  }

  /** The account's own ending, which depends on whether they ever arrived, as on the Users page. */
  function lifecycleAction(): RowAction {
    if (person.status === 'DEACTIVATED') {
      return { label: 'Reactivate', icon: UserCheck, onSelect: act(() => reactivate.mutate({ id: person.id }, said)), disabled: reactivate.isPending }
    }
    if (person.status === 'PENDING') {
      return {
        label: 'Delete invitation',
        icon: Trash2,
        destructive: true,
        onSelect: act(() => deleteInvited.mutate({ id: person.id }, said)),
        disabled: deleteInvited.isPending,
      }
    }
    return { label: 'Deactivate', icon: UserX, destructive: true, onSelect: act(() => deactivate.mutate({ id: person.id }, said)), disabled: deactivate.isPending }
  }

  const actions: RowAction[] = !manageable
    ? []
    : [
        ...(person.status === 'PENDING'
          ? [
              {
                label: resent ? 'Sent' : inviteActionLabel(person.invitedAt),
                icon: Mail,
                onSelect: act(() =>
                  resend.mutate(
                    { id: person.id },
                    {
                      onSuccess: () => {
                        setResent(true)
                        setFailure(null)
                      },
                      onError: said.onError,
                    },
                  ),
                ),
                disabled: resend.isPending || resent,
              } satisfies RowAction,
            ]
          : []),
        ...(everywhere ? [{ label: 'Change role', icon: Shield, onSelect: () => setChangingRole(true) } satisfies RowAction] : []),
        { label: 'Remove from this venue', icon: UserMinus, onSelect: act(() => unassign.mutate({ venueId, userId: person.id }, said)), disabled: unassign.isPending },
        ...(everywhere ? [lifecycleAction()] : []),
      ]

  return (
    <div className="px-3 py-3">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{person.name}</p>
          <p className="truncate text-xs text-ink-muted">{person.email}</p>
        </div>
        <span className="shrink-0 text-sm text-ink-muted">{personRoleLabel(person)}</span>
        <UserStatusBadge status={person.status} invitedAt={person.invitedAt} inviteExpiresAt={person.inviteExpiresAt} />
        {actions.length > 0 ? <RowActions label={person.name} actions={actions} /> : null}
      </div>
      {failure ? <FormError className="mt-1">{failure}</FormError> : null}
      {changingRole ? <ChangeRoleDialog person={person} roles={roles} open={changingRole} onOpenChange={setChangingRole} /> : null}
    </div>
  )
}

/**
 * A venue's staff: the people assigned to work at it, each with their role
 * (docs/custom-roles.spec.md). People View sees them. People Edit invites
 * someone to work here, adds someone already on staff, changes a role, takes
 * the venue off someone and deactivates them, within the guardrail the API
 * holds it to: only people whose role the viewer could give.
 *
 * Someone on a company-wide role works at every venue already, so isn't listed
 * here; staff on assigned venues manage their venues' staff from this section,
 * having no Users page.
 */
export function VenueStaff({ venue }: { venue: VenueDto }) {
  const { user } = useAuth()
  const edits = can(user, 'people:edit')
  const staffQuery = useVenueManagers(venue.id)
  const rolesQuery = useAssignableRoles()
  // Staff on assigned venues reach only their own; their switcher lists them.
  const assigned = worksAtAssignedVenues(user)
  const ownVenuesQuery = useSwitchableVenues()
  // Adding someone already on staff picks from the company's list, which takes a company-wide view of it.
  const addsExisting = edits && (isOrganizationAdmin(user) || reachesCompany(user))
  const usersQuery = useUsers({}, undefined, addsExisting)
  const assign = useAssignVenueManager()
  const [inviting, setInviting] = React.useState(false)
  const [selectedId, setSelectedId] = React.useState('')

  if (staffQuery.isPending || rolesQuery.isPending || (assigned && edits && ownVenuesQuery.isPending)) {
    return <Loader2 className="h-4 w-4 animate-spin text-ink-muted" />
  }
  if (staffQuery.isError) return <FormError>{requestErrorMessage(staffQuery.error)}</FormError>
  if (rolesQuery.isError) return <FormError>{requestErrorMessage(rolesQuery.error)}</FormError>

  const roles = rolesQuery.data
  const staff = staffQuery.data.managers
  const here = new Set(staff.map((m) => m.id))
  // Viewing this venue's settings, the viewer works where everyone listed does.
  const manageable = (person: UserDto): boolean => managesPerson(user, person, roles, true)
  const ownVenues = new Set((ownVenuesQuery.data?.venues ?? []).map((v) => v.id))
  const reachesAllOf = (person: UserDto): boolean => !assigned || person.managedVenueIds.every((id) => ownVenues.has(id))
  const eligible = (usersQuery.data?.users ?? []).filter(
    (u) => u.role === 'VENUE_ADMIN' && u.staffRole?.reach === 'venues' && !here.has(u.id) && manageable(u),
  )
  const invitesHere = edits && roles.roles.some((role) => role.mayGive && role.reach === 'venues')

  function onAssign(): void {
    if (selectedId === '') return
    assign.mutate({ venueId: venue.id, userId: selectedId }, { onSuccess: () => setSelectedId('') })
  }

  return (
    <div className="space-y-4">
      {staff.length === 0 ? (
        <p className="text-sm text-ink-muted">Nobody works here yet.</p>
      ) : (
        <div className="divide-y divide-line rounded-md border border-line">
          {staff.map((person) => (
            <StaffRow
              key={person.id}
              venueId={venue.id}
              person={person}
              roles={roles}
              manageable={manageable(person)}
              everywhere={reachesAllOf(person)}
            />
          ))}
        </div>
      )}

      {addsExisting && eligible.length > 0 ? (
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <Select aria-label="Someone on staff to add" value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
              <option value="">Add someone already on staff…</option>
              {eligible.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({personRoleLabel(u)})
                </option>
              ))}
            </Select>
          </div>
          <Button pending={assign.isPending} type="button" variant="outline" onClick={onAssign} disabled={selectedId === ''}>
            Add
          </Button>
        </div>
      ) : null}
      {assign.isError ? <FormError>{requestErrorMessage(assign.error)}</FormError> : null}

      {invitesHere ? (
        <Button type="button" size="sm" onClick={() => setInviting(true)}>
          <UserPlus className="h-4 w-4" /> Invite to {venue.name}
        </Button>
      ) : null}
      <Dialog open={inviting} onOpenChange={setInviting}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite to {venue.name}</DialogTitle>
            <DialogDescription>We’ll email them a link to set a password and join.</DialogDescription>
          </DialogHeader>
          {inviting ? <PersonInviteForm roles={roles} venue={{ id: venue.id, name: venue.name }} onDone={() => setInviting(false)} /> : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}
